import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  FiberManualRecord,
  Stop,
  Mic
} from '@mui/icons-material';
import { RecordingManager } from '../../services/recordingService';
import * as recordingService from '../../services/recordingService';
import ConsentDialog from './ConsentDialog';

const RecordingControls = ({ onRecordingComplete, onError }) => {
  const [recordingState, setRecordingState] = useState('idle'); // idle, requesting-consent, initializing, recording, stopping
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState('');
  const [showConsent, setShowConsent] = useState(false);
  const [recordingId, setRecordingId] = useState(null);
  
  const recordingManagerRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingManagerRef.current) {
        recordingManagerRef.current.cleanup();
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  // Update duration every second while recording
  useEffect(() => {
    if (recordingState === 'recording') {
      durationIntervalRef.current = setInterval(() => {
        if (recordingManagerRef.current) {
          setDuration(recordingManagerRef.current.getDuration());
        }
      }, 1000);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [recordingState]);

  const handleStartClick = () => {
    setShowConsent(true);
    setRecordingState('requesting-consent');
  };

  const handleConsentAccepted = async () => {
    setShowConsent(false);
    setRecordingState('initializing');
    setError('');

    try {
      // Initialize recording manager
      recordingManagerRef.current = new RecordingManager();
      await recordingManagerRef.current.initialize();

      // Start recording on backend
      const response = await recordingService.startRecording({
        title: `Live Recording ${new Date().toLocaleString()}`,
        description: 'Recording from browser',
        participants: [],
        consentGiven: true
      });

      setRecordingId(response.data.id);

      // Start actual recording
      recordingManagerRef.current.start();
      setRecordingState('recording');
      setDuration(0);

    } catch (err) {
      console.error('Error starting recording:', err);
      const errorMessage = err.message || 'Failed to start recording';
      setError(errorMessage);
      setRecordingState('idle');
      
      if (recordingManagerRef.current) {
        recordingManagerRef.current.cleanup();
        recordingManagerRef.current = null;
      }
      
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  const handleConsentDeclined = () => {
    setShowConsent(false);
    setRecordingState('idle');
  };

  const handleStopClick = async () => {
    if (!recordingManagerRef.current || !recordingId) {
      setError('No active recording');
      return;
    }

    setRecordingState('stopping');

    try {
      // Stop recording and get audio blob
      const audioBlob = await recordingManagerRef.current.stop();
      
      // Upload to backend
      const response = await recordingService.stopRecording(recordingId, audioBlob);
      
      // Cleanup
      recordingManagerRef.current.cleanup();
      recordingManagerRef.current = null;
      
      setRecordingState('idle');
      setRecordingId(null);
      setDuration(0);

      if (onRecordingComplete) {
        onRecordingComplete(response.data);
      }

    } catch (err) {
      console.error('Error stopping recording:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to stop recording';
      setError(errorMessage);
      setRecordingState('idle');
      
      if (recordingManagerRef.current) {
        recordingManagerRef.current.cleanup();
        recordingManagerRef.current = null;
      }
      
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Typography variant="h6" gutterBottom>
            Live Recording
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {recordingState === 'recording' && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <Chip
                icon={<FiberManualRecord />}
                label="Recording"
                color="error"
                sx={{
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 }
                  }
                }}
              />
              <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                {formatDuration(duration)}
              </Typography>
            </Box>
          )}

          {recordingState === 'initializing' && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography>Initializing microphone...</Typography>
            </Box>
          )}

          {recordingState === 'stopping' && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography>Saving recording...</Typography>
            </Box>
          )}

          <Box display="flex" gap={2}>
            {recordingState === 'idle' && (
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={<Mic />}
                onClick={handleStartClick}
              >
                Start Recording
              </Button>
            )}

            {recordingState === 'recording' && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Stop />}
                onClick={handleStopClick}
              >
                Stop Recording
              </Button>
            )}
          </Box>

          {recordingState === 'idle' && (
            <Typography variant="caption" color="text.secondary" align="center">
              Click "Start Recording" to begin capturing audio from your microphone.
              You will be asked for consent before recording begins.
            </Typography>
          )}
        </Box>
      </Paper>

      <ConsentDialog
        open={showConsent}
        onAccept={handleConsentAccepted}
        onDecline={handleConsentDeclined}
      />
    </>
  );
};

export default RecordingControls;
